// src/redux/slices/languagesSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_ROUTES from "../../config/apiRoutes";

// Map of local language files (static imports for reliability)
// Note: Run 'npm run download-languages' to download files to src/languages/
let localLanguageFiles = {};

// Try to load language files that exist locally
const tryLoadLocalFiles = async () => {
  try {
    // Try importing en.json
    const enModule = await import("../../languages/en.json").catch(() => null);
    if (enModule) localLanguageFiles.en = enModule.default || enModule;
  } catch (e) { /* file doesn't exist */ }
  
  try {
    // Try importing ar.json
    const arModule = await import("../../languages/ar.json").catch(() => null);
    if (arModule) localLanguageFiles.ar = arModule.default || arModule;
  } catch (e) { /* file doesn't exist */ }
};

// Initialize local files on load
tryLoadLocalFiles();

// Get local language file if available
const getLocalLanguageFile = (key) => {
  return localLanguageFiles[key] || null;
};

// Thunk: Fetch languages
export const fetchLanguages = createAsyncThunk(
  "languages/fetchLanguages",
  async ({ currentPage = 1, limit = 10 } = {}, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().auth;
      const filterPayload = {
        currentPage,
        limit,
      };
      const response = await axios.post(
        API_ROUTES.LANGUAGES.GET_ALL,
        filterPayload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch languages"
      );
    }
  }
);

// Thunk: Create language (with file upload)
export const createLanguage = createAsyncThunk(
  "languages/createLanguage",
  async (formData, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().auth;
      const response = await axios.post(
        API_ROUTES.LANGUAGES.CREATE,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create language"
      );
    }
  }
);

// Thunk: Update language (with file upload)
export const updateLanguage = createAsyncThunk(
  "languages/updateLanguage",
  async ({ key, formData }, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().auth;
      const response = await axios.patch(
        API_ROUTES.LANGUAGES.UPDATE(key),
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update language"
      );
    }
  }
);

// Thunk: Delete language by key
export const deleteLanguage = createAsyncThunk(
  "languages/deleteLanguage",
  async (key, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().auth;
      await axios.delete(
        API_ROUTES.LANGUAGES.DELETE(key),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Note: Local file will need to be deleted manually via the download script
      return key;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete language"
      );
    }
  }
);

// Thunk: Load all language files from local folder
export const loadAllLocalLanguageFiles = createAsyncThunk(
  "languages/loadAllLocalLanguageFiles",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { languages, cachedContent } = getState().languages;
      
      const loadedFiles = {};
      const errors = [];
      const needsDownload = [];

      // Try to load each language from local files
      for (const lang of languages) {
        // Skip if already in state
        if (cachedContent[lang.key] && Object.keys(cachedContent[lang.key]).length > 0) {
          console.log(`Language ${lang.key} already loaded, skipping...`);
          loadedFiles[lang.key] = cachedContent[lang.key];
          continue;
        }
        
        // Try to get from local files
        const content = getLocalLanguageFile(lang.key);
        
        if (content && typeof content === 'object' && !Array.isArray(content) && Object.keys(content).length > 0) {
          loadedFiles[lang.key] = content;
          console.log(`Loaded ${lang.key} from local file with ${Object.keys(content).length} keys`);
        } else {
          console.log(`Local file not found for ${lang.key}, will download from server`);
          needsDownload.push(lang);
        }
      }

      console.log('Load complete. Loaded:', Object.keys(loadedFiles), 'Needs download:', needsDownload.map(l => l.key));
      return { loadedFiles, errors, needsDownload };
    } catch (error) {
      console.error('loadAllLocalLanguageFiles error:', error);
      return rejectWithValue(error.message || "Failed to load language files");
    }
  }
);

// Thunk: Download and cache all language file contents from server
export const downloadAllLanguageFiles = createAsyncThunk(
  "languages/downloadAllLanguageFiles",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { languages, cachedContent } = getState().languages;
      
      const downloadedFiles = {};
      const errors = [];
      const skipped = [];

      // Download each language file from its URL
      for (const lang of languages) {
        // Skip if no URL
        if (!lang.url) {
          console.warn(`Language ${lang.key} has no URL, skipping...`);
          errors.push({ key: lang.key, error: 'No URL provided' });
          continue;
        }
        
        // Skip if already cached (deduplication)
        if (cachedContent[lang.key] && Object.keys(cachedContent[lang.key]).length > 0) {
          console.log(`Language ${lang.key} already cached, skipping download...`);
          skipped.push(lang.key);
          continue;
        }
        
        try {
          console.log(`Downloading language file for ${lang.key} from: ${lang.url}`);
          
          // Use fetch instead of axios to avoid CORS preflight issues
          const response = await fetch(lang.url, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache',
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const content = await response.json();
          
          console.log(`Successfully downloaded ${lang.key}, keys count:`, Object.keys(content).length);
          
          if (content && typeof content === 'object' && !Array.isArray(content)) {
            // Store in Redux state (local files are managed via npm script)
            downloadedFiles[lang.key] = content;
            console.log(`Loaded ${lang.key} with ${Object.keys(content).length} keys`);
          } else {
            console.warn(`Invalid content format for ${lang.key}:`, typeof content);
            errors.push({ key: lang.key, error: 'Invalid content format' });
          }
        } catch (err) {
          console.error(`Failed to download language file for ${lang.key}:`, err.message);
          errors.push({ key: lang.key, error: err.message });
        }
      }

      console.log('Download complete. Downloaded:', Object.keys(downloadedFiles), 'Skipped:', skipped, 'Errors:', errors);
      return { downloadedFiles, errors, skipped };
    } catch (error) {
      console.error('downloadAllLanguageFiles error:', error);
      return rejectWithValue(error.message || "Failed to download language files");
    }
  }
);

// Thunk: Download a single language file (force refresh from server)
export const downloadLanguageFile = createAsyncThunk(
  "languages/downloadLanguageFile",
  async (language, { getState, rejectWithValue }) => {
    try {
      if (!language.url) {
        return rejectWithValue('No URL provided for this language');
      }
      
      console.log(`Downloading single language file for ${language.key} from: ${language.url}`);
      
      // Use fetch instead of axios to avoid CORS preflight issues
      const response = await fetch(language.url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const content = await response.json();
      
      console.log(`Successfully downloaded ${language.key}, keys count:`, Object.keys(content).length);
      
      if (!content || typeof content !== 'object' || Array.isArray(content)) {
        console.error('Invalid content received:', typeof content);
        return rejectWithValue('Invalid content format received from server');
      }
      
      console.log(`Loaded ${language.key} with ${Object.keys(content).length} keys`);
      
      return { key: language.key, content };
    } catch (error) {
      console.error('Download language file error:', error);
      return rejectWithValue(error.message || "Failed to download language file");
    }
  }
);

// Thunk: Get language content from local file or download if not available
export const getLanguageContent = createAsyncThunk(
  "languages/getLanguageContent",
  async (language, { getState, dispatch, rejectWithValue }) => {
    try {
      // First try to load from local files
      const localContent = getLocalLanguageFile(language.key);
      
      if (localContent && typeof localContent === 'object' && Object.keys(localContent).length > 0) {
        console.log(`Loaded ${language.key} from local file`);
        return { key: language.key, content: localContent, fromLocal: true };
      }
      
      // If not available locally, download from server
      const result = await dispatch(downloadLanguageFile(language)).unwrap();
      return { ...result, fromLocal: false };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Thunk: Save language content (update server and Redux state)
export const saveLanguageContent = createAsyncThunk(
  "languages/saveLanguageContent",
  async ({ key, content }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { token } = getState().auth;
      
      // Create JSON file from content
      const jsonContent = JSON.stringify(content, null, 2);
      const blob = new Blob([jsonContent], { type: "application/json" });
      const file = new File([blob], `${key}.json`, { type: "application/json" });

      const formData = new FormData();
      formData.append("file", file);

      // Send to server
      const response = await axios.patch(
        API_ROUTES.LANGUAGES.UPDATE(key),
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      // Note: To update local files, run: npm run download-languages
      // This will re-download all language files from server

      return { key, content, response: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to save language content"
      );
    }
  }
);

const languagesSlice = createSlice({
  name: "languages",
  initialState: {
    languages: [],
    cachedContent: {}, // Store downloaded language content by key
    total: 0,
    totalPages: 0,
    currentPage: 1,
    loading: false,
    downloading: false,
    saving: false,
    error: null,
    downloadProgress: { total: 0, completed: 0 },
  },
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setCachedContent: (state, action) => {
      const { key, content } = action.payload;
      state.cachedContent[key] = content;
    },
    clearCachedContent: (state) => {
      state.cachedContent = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchLanguages cases
      .addCase(fetchLanguages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLanguages.fulfilled, (state, action) => {
        state.loading = false;
        state.languages = action.payload.data || [];
        state.total = action.payload.total || 0;
        state.totalPages = action.payload.totalPages || 0;
      })
      .addCase(fetchLanguages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // createLanguage cases
      .addCase(createLanguage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLanguage.fulfilled, (state, action) => {
        state.loading = false;
        const newLanguage = action.payload.data || action.payload;
        state.languages.unshift(newLanguage);
        state.total += 1;
      })
      .addCase(createLanguage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // updateLanguage cases
      .addCase(updateLanguage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLanguage.fulfilled, (state, action) => {
        state.loading = false;
        const updatedLanguage = action.payload.data || action.payload;
        const index = state.languages.findIndex(
          (lang) => lang.key === updatedLanguage.key
        );
        if (index !== -1) {
          state.languages[index] = updatedLanguage;
        }
      })
      .addCase(updateLanguage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // deleteLanguage cases
      .addCase(deleteLanguage.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteLanguage.fulfilled, (state, action) => {
        state.loading = false;
        state.languages = state.languages.filter(
          (lang) => lang.key !== action.payload
        );
        state.total -= 1;
        // Also remove from cached content
        delete state.cachedContent[action.payload];
      })
      .addCase(deleteLanguage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // downloadAllLanguageFiles cases
      .addCase(downloadAllLanguageFiles.pending, (state) => {
        state.downloading = true;
        state.error = null;
      })
      .addCase(downloadAllLanguageFiles.fulfilled, (state, action) => {
        state.downloading = false;
        // Update cached content with downloaded files
        Object.entries(action.payload.downloadedFiles).forEach(([key, content]) => {
          state.cachedContent[key] = content;
        });
      })
      .addCase(downloadAllLanguageFiles.rejected, (state, action) => {
        state.downloading = false;
        state.error = action.payload;
      })
      // loadAllLocalLanguageFiles cases
      .addCase(loadAllLocalLanguageFiles.pending, (state) => {
        state.downloading = true;
        state.error = null;
      })
      .addCase(loadAllLocalLanguageFiles.fulfilled, (state, action) => {
        state.downloading = false;
        // Update cached content with loaded files
        Object.entries(action.payload.loadedFiles).forEach(([key, content]) => {
          state.cachedContent[key] = content;
        });
      })
      .addCase(loadAllLocalLanguageFiles.rejected, (state, action) => {
        state.downloading = false;
        state.error = action.payload;
      })
      // downloadLanguageFile cases
      .addCase(downloadLanguageFile.pending, (state) => {
        state.downloading = true;
      })
      .addCase(downloadLanguageFile.fulfilled, (state, action) => {
        state.downloading = false;
        state.cachedContent[action.payload.key] = action.payload.content;
      })
      .addCase(downloadLanguageFile.rejected, (state, action) => {
        state.downloading = false;
        state.error = action.payload;
      })
      // getLanguageContent cases
      .addCase(getLanguageContent.pending, (state) => {
        state.downloading = true;
      })
      .addCase(getLanguageContent.fulfilled, (state, action) => {
        state.downloading = false;
        state.cachedContent[action.payload.key] = action.payload.content;
      })
      .addCase(getLanguageContent.rejected, (state, action) => {
        state.downloading = false;
        state.error = action.payload;
      })
      // saveLanguageContent cases
      .addCase(saveLanguageContent.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveLanguageContent.fulfilled, (state, action) => {
        state.saving = false;
        state.cachedContent[action.payload.key] = action.payload.content;
      })
      .addCase(saveLanguageContent.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export const { setCurrentPage, setCachedContent, clearCachedContent } = languagesSlice.actions;
export default languagesSlice.reducer;
